import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import queryString from 'query-string'
import Button from '../_library/Button'
import Field from '../_library/Field'
import { deleteCookieByName, getCookieByName } from '../utils/cookies'
import { CLEAR_PENDING_TWO_FACTOR, FETCH_SESSION } from '../../_common/redux/auth/actions'
import { sendTwoFactorCodeViaSms, verifyTwoFactorCode } from './twoFactorApi'

const COOKIE_NAME = 'X-TF-ECOMMERCE'

const safeNextPath = next => {
  if (typeof next !== 'string' || !next.startsWith('/') || next.startsWith('//')) {
    return '/'
  }
  return next
}

const errorMessage = caught => {
  const data = caught && caught.response && caught.response.data
  return data && typeof data.message === 'string' ? data.message : undefined
}

const errorCode = caught => {
  const data = caught && caught.response && caught.response.data
  return data && typeof data.code === 'string' ? data.code : undefined
}

const errorStatus = caught => {
  const status = caught && caught.response && caught.response.status
  return typeof status === 'number' ? status : undefined
}

const TwoFactorPage = ({ history, location, user, FETCH_SESSION: fetchSession, CLEAR_PENDING_TWO_FACTOR: clearPending }) => {
  const { next } = queryString.parse((location && location.search) || '')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [smsMessage, setSmsMessage] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [sendingSms, setSendingSms] = useState(false)
  const [showSms, setShowSms] = useState(true)

  useEffect(() => {
    document.title = 'Verify sign-in'
  }, [])

  useEffect(() => {
    if (user) {
      history.replace(safeNextPath(next))
      return
    }
    if (!getCookieByName(COOKIE_NAME)) {
      clearPending()
      history.replace('/signin')
    }
  }, [clearPending, history, next, user])

  const handleBackToSignIn = () => {
    deleteCookieByName(COOKIE_NAME)
    clearPending()
    history.replace('/signin')
  }

  const handleVerify = async event => {
    event.preventDefault()
    const trimmed = code.replace(/\s+/g, '')
    if (!trimmed) {
      setError('Enter the code from your authenticator app.')
      return
    }

    setVerifying(true)
    setError('')
    setSmsMessage('')

    try {
      await verifyTwoFactorCode(trimmed)
      await fetchSession({ hideError: true, noAppcues: true, throwOnError: true })
      history.replace(safeNextPath(next))
    } catch (caught) {
      const status = errorStatus(caught)
      const message = errorMessage(caught)
      const errCode = errorCode(caught)

      if (errCode === 'two_factor_auth_locked') {
        deleteCookieByName(COOKIE_NAME)
        clearPending()
        setError(message || 'Too many attempts. Sign in again.')
        return
      }

      if (status === 401 || (status === 422 && message === 'Unauthorized')) {
        deleteCookieByName(COOKIE_NAME)
        clearPending()
        setError(message && message !== 'Unauthorized' ? message : 'This sign-in expired. Sign in again.')
        return
      }

      setError(message || 'That code did not work. Try again.')
    } finally {
      setVerifying(false)
    }
  }

  const handleSendSms = async () => {
    setSendingSms(true)
    setError('')
    setSmsMessage('')

    try {
      const res = await sendTwoFactorCodeViaSms()
      setSmsMessage(typeof res.message === 'string' ? res.message : 'We texted a code to your phone.')
    } catch (caught) {
      const message = errorMessage(caught)
      const status = errorStatus(caught)

      if (status === 422 && message && /phone/i.test(message)) {
        setShowSms(false)
      }

      setError(message || 'Could not text a code. Use your authenticator app.')
    } finally {
      setSendingSms(false)
    }
  }

  return (
    <div className="loginpage">
      <form onSubmit={handleVerify}>
        <div className="login_holder">
          <div className="login_content clearfix">
            <div className="login_card">
              <div className="login_data">
                <div className="login_logo text-center">
                  <img alt="logo" src={asset('/resources/images/tf-logo.png')} style={{ width: 80 }} />
                </div>
                <h2 className="heading">Check your authenticator</h2>
                <p className="two-factor-copy">
                  Enter the 6-digit code from your authenticator app to finish signing in.
                </p>
                <div className="form_holder">
                  {error ? <div className="alert alert-danger">{error}</div> : null}
                  {smsMessage ? <div className="alert alert-success">{smsMessage}</div> : null}
                  <Field
                    id="two-factor-code"
                    label="Verification code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    placeholder="123456"
                    value={code}
                    onChange={event => setCode(event.target.value)}
                  />
                  <div className="text-center">
                    <Button type="submit" className="login_btn" loading={verifying} disabled={sendingSms}>
                      Verify
                    </Button>
                  </div>
                  {showSms ? (
                    <div className="text-center">
                      <Button
                        type="button"
                        className="login_btn passkey_btn"
                        loading={sendingSms}
                        disabled={verifying}
                        onClick={handleSendSms}
                      >
                        Text me a code
                      </Button>
                    </div>
                  ) : null}
                  <div aria-hidden className="restore-password-show" onClick={handleBackToSignIn}>
                    Back to Log In
                  </div>
                  <div className="copy_right">
                    Copyright &copy; {getCurrentYear()} THE<strong>TICKET</strong>FAIRY, Inc.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export default connect(
  state => ({
    user: state.auth.user,
  }),
  { FETCH_SESSION, CLEAR_PENDING_TWO_FACTOR },
)(TwoFactorPage)
